import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import AppearanceToggle from '../../components/UI/AppearanceToggle';
import { getRolePrefix, getRoleBasedRoute } from "../../utils/routeUtils";

import {
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const Sidebar = () => {
  const { roles } = useContext(AuthContext);
  const location = useLocation();
  const path = location.pathname;
  const [theme, setTheme] = useState(localStorage.getItem("theme"));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  
  // Helper function to generate role-based routes
  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setTheme(localStorage.getItem("theme"));
    };

    // Listen for storage changes (if changed from other tabs)
    window.addEventListener("storage", handleStorageChange);

    // Also update on mount
    const interval = setInterval(() => {
      if (localStorage.getItem("theme") !== theme) {
        setTheme(localStorage.getItem("theme"));
      }
    }, 100);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [theme]);

  // Auto-expand recruitment menu when on recruitment pages
  useEffect(() => {
    if (isRecruitmentPath()) {
      setActiveMenu("recruitment");
      if (location.pathname.includes('/interviews') || location.pathname.includes('/scheduled-interviews') || location.pathname.includes('/my-scheduled-interviews')) {
        setActiveSubMenu("interviews");
      }
    } else if (isFileManagementPath()) {
      setActiveMenu("file-management");
    } else if (isSettingsPath()) {
      setActiveMenu("settings");
    } else if (isMasterPath()) {
      setActiveMenu("master");
    } else if (isPayrollPath()) {
      setActiveMenu("payroll");
    } else if (isUsersPath()) {
      setActiveMenu("users");
    }
  }, [location.pathname]);

  const toggleSidebar = () => setIsCollapsed(prev => !prev);
  const toggleMenu = (menu) => {
    setActiveMenu(prev => (prev === menu ? null : menu));
    setActiveSubMenu(null);
  };
  const toggleSubMenu = (subMenu) => {
    setActiveSubMenu(prev => (prev === subMenu ? null : subMenu));
  };
  const isActive = (path) => location.pathname === path;
  
  // Check if a submenu item is active
  const isSubMenuActive = (path) => {
    const roleBasedPath = getRoleBasedRouteLocal(path);
    return location.pathname === roleBasedPath;
  };
  
  // Check if current path is a recruitment-related path
  const isRecruitmentPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const recruitmentPaths = [
      `${rolePrefix}/recruitment-dashboard`,
      `${rolePrefix}/hiringrequests`,
      `${rolePrefix}/hod-requests`,
      `${rolePrefix}/recruitment-response`,
      `${rolePrefix}/campaigns`,
      `${rolePrefix}/formlist`,
      `${rolePrefix}/form_response`,
      `${rolePrefix}/candidates`,
      `${rolePrefix}/interviews`,
      `${rolePrefix}/scheduled-interviews`,
      `${rolePrefix}/my-scheduled-interviews`,
      `${rolePrefix}/application_form_response`,
      `${rolePrefix}/resignation`,
      '/admin/recruitment-dashboard',
      '/admin/hod-requests',
      '/admin/recruitment-response',
      '/admin/campaigns',
      '/admin/formlist',
      '/admin/form_response',
      '/admin/candidates',
      '/admin/interviews',
      '/admin/scheduled-interviews',
      '/admin/my-scheduled-interviews',
      '/recruitment-dashboard',
      '/hod-requests',
      '/recruitment-response',
      '/campaigns',
      '/formlist',
      '/form_response',
      '/candidates',
      '/interviews',
      '/scheduled-interviews',
      '/my-scheduled-interviews'
    ];
    return recruitmentPaths.some(path => location.pathname === path);
  };

  // Check if current path is a file management-related path
  const isFileManagementPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const fileManagementPaths = [
      `${rolePrefix}/upload-resumes`,
      `${rolePrefix}/doc-generator`,
      `${rolePrefix}/manage-files`,
      '/admin/upload-resumes',
      '/admin/doc-generator',
      '/admin/manage-files',
      '/upload-resumes',
      '/doc-generator',
      '/manage-files'
    ];
    return fileManagementPaths.some(path => location.pathname === path);
  };

  // Check if current path is a settings-related path
  const isSettingsPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const settingsPaths = [
      `${rolePrefix}/roles`,
      `${rolePrefix}/permissions`,
      '/admin/roles',
      '/admin/permissions',
      '/roles',
      '/permissions'
    ];
    return settingsPaths.some(path => location.pathname === path);
  };

  // Check if current path is a master-related path
  const isMasterPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const masterPaths = [
      `${rolePrefix}/master/employment-type`,
      `${rolePrefix}/master/departments`,
      `${rolePrefix}/master/designations`,
      `${rolePrefix}/master/workplace`,
      `${rolePrefix}/master/leave-type`,
      `${rolePrefix}/master/shifts`,
      `${rolePrefix}/master/holidays`,
      `${rolePrefix}/master/leave-balance-master`,
      `${rolePrefix}/master/forms-master`,
      `${rolePrefix}/master/department-hods`,
      `${rolePrefix}/master/state_code`,
      `${rolePrefix}/master/employee-grades`,
      `${rolePrefix}/master/categories`,
      `${rolePrefix}/master/salary-grade`,
      `${rolePrefix}/master/approval-level`,
      `${rolePrefix}/master/approver-modules`,
      `${rolePrefix}/master/employee-approver`,
      `${rolePrefix}/master/salary-component`,
      `${rolePrefix}/master/pay-structure`,
      `${rolePrefix}/master/eligibility-rule`,

      '/admin/master/employment-type',
      '/admin/master/departments',
      '/admin/master/designations',
      '/admin/master/workplace',
      '/admin/master/leave-type',
      '/admin/master/shifts',
      '/admin/master/holidays',
      '/admin/master/leave-balance-master',
      '/admin/master/forms-master',
      '/admin/master/department-hods',
      '/master/employment-type',
      '/master/departments',
      '/master/designations',
      '/master/workplace',
      '/master/leave-type',
      '/master/shifts',
      '/master/holidays',
      '/master/leave-balance-master',
      '/master/forms-master',
      '/master/department-hods',

    ];
    return masterPaths.some(path => location.pathname === path);
  };

  // Check if current path is a payroll-related path
  const isPayrollPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollPaths = [
      `${rolePrefix}/payroll/dashboard`,
      `${rolePrefix}/payroll/employee-details`,
      `${rolePrefix}/payroll/employment-details-management`,
      `${rolePrefix}/payroll/attendance-leave`,
      `${rolePrefix}/payroll/payroll-processing`,
      `${rolePrefix}/payroll/payslip-generation`,
      `${rolePrefix}/payroll/salary-structure`,
      `${rolePrefix}/payroll/salary-disbursement`,
      `${rolePrefix}/payroll/statutory-compliance`,
      `${rolePrefix}/payroll/reports`,
      `${rolePrefix}/payroll/settings`,
      `${rolePrefix}/master/employee-salary-assignment`,

      '/admin/payroll/dashboard',
      '/admin/payroll/employee-details',
      '/admin/payroll/employment-details-management',
      '/admin/payroll/attendance-leave',
      '/admin/payroll/payroll-processing',
      '/admin/payroll/payslip-generation',
      '/admin/payroll/salary-structure',
      '/admin/payroll/salary-disbursement',
      '/admin/payroll/statutory-compliance',
      '/admin/payroll/reports',
      '/admin/payroll/settings',
      '/payroll/dashboard',
      '/payroll/employee-details',
      '/payroll/employment-details-management',
      '/payroll/attendance-leave',
      '/payroll/payroll-processing',
      '/payroll/payslip-generation',
      '/payroll/salary-structure',
      '/payroll/salary-disbursement',
      '/payroll/statutory-compliance',
      '/payroll/reports',
      '/payroll/settings'
    ];
    return payrollPaths.some(path => location.pathname === path);
  };

  // Check if current path is a users-related path
  const isUsersPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const usersPaths = [
      `${rolePrefix}/users`,
      `${rolePrefix}/emergency-contacts`,
      '/admin/users',
      '/admin/emergency-contacts',
      '/users',
      '/emergency-contacts'
    ];
    return usersPaths.some(path => location.pathname === path);
  };

  // Check if current path is an attendance-related path
  const isAttendancePath = () => {
    const attendanceRoutes = [
      'attendance_management',
      'leave_page',
      'od',
      'co+',
      'leave-balance',
      'check-leave-balance',
      'public-calender',
      'dashboard',
      'attendance',
      'check-attendance',
      'attendance-upload',
      'mob-attendance',
      'swipe',
      'short-time-off',
      'shift-change',
      'pending-checkin',
      'pending-checkout',
      'leave-requests'
    ];
    
    return attendanceRoutes.some(route => location.pathname.includes(route));
  };

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        className="md:hidden absolute md:top-4 top-1 left-2 mt-1 z-0 p-2 bg-blue-800 hover:bg-blue-900 hover:ring-2 ring-blue-200 transition delay-75 rounded-lg"
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu">
        <svg data-slot="icon" className="w-6 h-6 text-white" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </button>

      {/* Sidebar Drawer */}
      {/* Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-75 md:hidden"
        ></div>
      )}

      <div
        className={
          `fixed inset-y-0 left-0 z-20 border-r-gray-800 dark:bg-black bg-white text-gray-800 shadow-lg
          transform transition-transform duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static
          h-screen max-h-screen
          ${isCollapsed ? "w-24" : "md:w-[295px] border-r-none"}
          overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-200 scrollbar-hide`
        }>
        {/* Header (Collapse + Mobile Close) */}
        <div className={`${isCollapsed ? "p-4" : "md:p-[18px] p-[22px]"} flex items-center justify-between md:bg-white dark:bg-black bg-white text-white`}>
          {isCollapsed ? (
            <div
              {...(!mobileOpen && { onClick: toggleSidebar })}
              className="flex items-center md:cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition delay-100 px-6 py-4">
                <span
                  className={`${isCollapsed ? "flex justify-center w-full text-2xl text-black dark:text-gray-400" : "text-2xl font-bold tracking-wide text-black dark:text-white"}`}>
                   <FaChevronRight className="w-5 h-5 dark:text-white" />
                </span>
            </div>
          ) : (  
            <div
              className="flex items-center justify-center gap-2 mx-auto">
                <div className="bg-[#2563EB] p-1 rounded-md">
                  <svg className="w-8 h-8 text-white" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </div>
              <span
                className={`${isCollapsed ? "flex justify-center w-full text-2xl" : "text-2xl dark:text-white text-black font-poppins font-semibold tracking-wide mr-14"}`}
              > 
                HRMS
              </span>

            </div>
          )}

          {!isCollapsed && (
              <span className="hidden md:block ml-2 cursor-pointer p-3 text-black hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full transition delay-100" 
              {...(!mobileOpen && { onClick: toggleSidebar })}>
                {isCollapsed ? <FaChevronDown /> : <FaChevronLeft className="w-5 h-5 dark:text-white" />}
              </span>
            )}

          {/* Close on Mobile */}
          <button
            className="md:hidden text-black dark:text-white p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition delay-100"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <FaTimes className="text-black dark:text-white" size={20} />
          </button>
        </div>

        {/* Sidebar Links */}
        <ul className="mt-6 space-y-2">

          {/* Overview */}
          {!(roles.length === 1 && roles[0] === "User") && (
            <li className="px-2">
              {!isCollapsed && (<p className="px-3 font-poppins dark:text-white font-semibold tracking-wide mb-2">GENERAL</p>)}
              <Link
                to={
                  roles.includes("Admin")
                    ? "/admin"
                    : roles.includes("Manager")
                    ? "/manager"
                    : roles.includes("HR")
                    ? "/hr"
                    : roles.includes("HOD")
                    ? "/hod"
                    : "/user"
                }
                className={`flex items-center gap-4 mx-2 px-4 py-2 rounded-md transition ${isCollapsed ? "justify-center" : ""} ${isActive(
                  roles.includes("Admin")
                    ? "/admin"
                    : roles.includes("Manager")
                    ? "/manager"
                    : roles.includes("HR")
                    ? "/hr"
                    : roles.includes("HOD")
                    ? "/hod"
                    : "/user"
                )
                  ? "bg-[#0077FE] text-white "
                  : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900"}`}
                onClick={() => setMobileOpen(false)}
              >
                <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-home"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l-2 0l9 -9l9 9l-2 0" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" /><path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" /></svg>
                {!isCollapsed && <span className="font-poppins font-semibold">Overview</span>}
              </Link>
            </li>
          )}

          {/* On-boarding Form response */}
          {roles.includes("HR") && (
            <li className="px-3">
              <Link
                to={getRoleBasedRouteLocal("/admin/on_boarding_form_response")}
                onClick={ () => setMobileOpen(false)}
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md transition ${isCollapsed ? "justify-center" : ""} ${isActive(getRoleBasedRouteLocal("/admin/on_boarding_form_response")) ? "bg-[#0077FE] text-white font-sans font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
              >
                <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-user-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" /><path d="M16 19h6" /><path d="M19 16v6" /><path d="M6 21v-2a4 4 0 0 1 4 -4h4" /></svg>
                {!isCollapsed && <span>On-boarding</span>}
              </Link>
            </li>
          )}

          {/* Users */}
          {['Admin','HR'].some(r => roles.includes(r)) && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${isCollapsed ? "justify-center" : ""} ${activeMenu === "users" || isUsersPath() ? "bg-[#0077FE] text-white font-poppins font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
                onClick={() => toggleMenu('users')}
              >
                <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-users"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0 -3 -3.85" /></svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Users
                    {activeMenu === 'users' || isUsersPath() ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === 'users' && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    { label: "All Users", path: "/admin/users" },
                    { label: "Emergency Contacts", path: "/admin/emergency-contacts" },
                  ].map(item => (
                    <Link to={getRoleBasedRouteLocal(item.path)} key={item.path}>
                      <li
                        onClick={() => setMobileOpen(false)}
                        className={`py-2 px-2 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${isActive(getRoleBasedRouteLocal(item.path)) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}
                      >
                        {item.label}
                      </li>
                    </Link>
                  ))}
                </ul>
              )}
            </li>
          )}

          {/* Attendance Management Section */}
          {['Manager','HR', "User", "Admin", "HOD"].some(r => roles.includes(r)) && (
            <li className="px-3">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md transition ${isCollapsed ? "justify-center" : ""} ${isAttendancePath() ? "bg-[#0077FE] text-white font-poppins font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
                onClick={() => setMobileOpen(false)}
              >
                <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-calendar-week"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" /><path d="M16 3v4" /><path d="M8 3v4" /><path d="M4 11h16" /><path d="M7 14h.013" /><path d="M10.01 14h.005" /><path d="M13.01 14h.005" /><path d="M16.015 14h.005" /><path d="M13.015 17h.005" /><path d="M7.01 17h.005" /><path d="M10.01 17h.005" /></svg>
                {!isCollapsed && <span>Attendance</span>}
              </Link>
            </li>
          )}

          {/* Payroll Section */}
          {['HR'].some(r => roles.includes(r)) && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${isCollapsed ? "justify-center" : ""} ${activeMenu === "payroll" || isPayrollPath() ? "bg-[#0077FE] text-white font-poppins font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
                onClick={() => toggleMenu('payroll')}
              >
                <svg  xmlns="http://www.w3.org/2000/svg"  width="26"  height="26"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-paywall"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M13 21h-6a2 2 0 0 1 -2 -2v-6a2 2 0 0 1 2 -2h10" /><path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" /><path d="M8 11v-4a4 4 0 1 1 8 0v4" /><path d="M21 15h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5" /><path d="M19 21v1" /><path d="M19 14v1" /></svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full font-sans font-semibold">
                    Payroll
                    {activeMenu === 'payroll' || isPayrollPath() ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === 'payroll' && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    { label: "Dashboard", path: "/admin/payroll/dashboard" },
                    { label: "Employee Details", path: "/admin/payroll/employee-details" },
                    { label: "Employment Details", path: "/admin/payroll/employment-details-management" },
                    { label: "Attendance Leave", path: "/admin/payroll/attendance-leave" },
                    { label: "Salary Assignment", path: "/admin/master/employee-salary-assignment" },
                    { label: "Payroll Processing", path: "/admin/payroll/payroll-processing" },
                    { label: "Payslip Generation", path: "/admin/payroll/payslip-generation" },
                    { label: "Salary Structure", path: "/admin/payroll/salary-structure" },
                    { label: "Salary Disbursement", path: "/admin/payroll/salary-disbursement" },
                    { label: "Statutory Compliance", path: "/admin/payroll/statutory-compliance" },
                    { label: "Reports", path: "/admin/payroll/reports" },
                    { label: "Settings", path: "/admin/payroll/settings" },
                    ].map(item => (
                     <Link to={getRoleBasedRouteLocal(item.path)} key={item.path}>
                       <li
                         onClick={() => setMobileOpen(false)}
                         className={`py-2 px-2 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 my-1 dark:hover:bg-gray-900 rounded-md ${isActive(getRoleBasedRouteLocal(item.path)) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}
                       >
                         {item.label}
                       </li>
                     </Link>
                   ))}
                 </ul>
              )}
            </li>
          )}

          {/* Recruitment */}
          {['Manager','HR', 'HOD'].some(r => roles.includes(r)) && (
            <li className="px-2 mb-2"> 
              {!isCollapsed && (<p className="px-2 font-poppins dark:text-white font-semibold tracking-wide mb-2">MANAGEMENT</p>)}
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${isCollapsed ? "justify-center" : ""} ${activeMenu === "recruitment" || isRecruitmentPath() ? "bg-[#0077FE] text-white font-sans font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
                onClick={() => toggleMenu('recruitment')}>

                <svg  xmlns="http://www.w3.org/2000/svg"  width="27"  height="27"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-replace-user"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M21 11v-3c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586h-6m0 0l3 3m-3 -3l3 -3" /><path d="M3 13.013v3c0 .53 .211 1.039 .586 1.414c.375 .375 .884 .586 1.414 .586h6m0 0l-3 -3m3 3l-3 3" /><path d="M16 16.502c0 .53 .211 1.039 .586 1.414c.375 .375 .884 .586 1.414 .586c.53 0 1.039 -.211 1.414 -.586c.375 -.375 .586 -.884 .586 -1.414c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414z" /><path d="M4 4.502c0 .53 .211 1.039 .586 1.414c.375 .375 .884 .586 1.414 .586c.53 0 1.039 -.211 1.414 -.586c.375 -.375 .586 -.884 .586 -1.414c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414z" /><path d="M21 21.499c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586h-2c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414" /><path d="M9 9.499c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586h-2c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414" /></svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Recruitment
                    {activeMenu === 'recruitment' || isRecruitmentPath() ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                )}

              </div>
              {!isCollapsed && activeMenu === 'recruitment' && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    { label: "Dashboard", path: "/admin/recruitment-dashboard", roles: ['HR', 'Manager'] },
                    { label: "Requests", path: "/admin/hiringrequests", roles: ['HR', 'Manager'] },
                    { label: "HOD Requests", path: "/admin/hod-requests", roles: ['HOD'] },
                    { label: "Application Responses", path: "/admin/application_form_response", roles: ['HR'] },
                    // { label: "Recruitment Responses", path: "/admin/recruitment-response", roles: ['HR', 'Manager']},
                    // { label: "My Hiring Requests", path: "/admin/myrequests", roles: ['HR', 'Manager'] },
                    // { label: "View Requests", path: "/admin/viewrequests", roles: ['Manager','HR'] },
                    { label: "Campaigns", path: "/admin/campaigns", roles: ['HR', 'Manager'] },
                    { label: "Forms", path: "/admin/formlist", roles: ['HR'] },
                    { label: "Form Responses", path: "/admin/form_response", roles: ['HR'] },
                    { label: "All Candidates", path: "/admin/candidates", roles: ['HR'] },
                    // { label: "Offer Letters", path: "/admin/offer-letters", roles: ['HR', 'Manager'] },
                    // { label: "Offer Templates", path: "/admin/offer-templates", roles: ['HR', 'Manager'] },
                    { label: "Interviews", path: "/admin/interviews", roles: ['HR', 'Manager'], hasSubmenu: true },
                    { label: "Resignations", path: "/admin/resignation", roles: ['HR', 'Manager']},
                    
                    ].filter(item => !item.roles || item.roles.some(r => roles.includes(r))).map(item => (
                     item.hasSubmenu ? (
                       <div key={item.path}>
                         <div
                           className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md cursor-pointer ${isActive(getRoleBasedRouteLocal(item.path)) || isActive(getRoleBasedRouteLocal("/admin/scheduled-interviews")) || isActive(getRoleBasedRouteLocal("/admin/my-scheduled-interviews")) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}
                           onClick={() => toggleSubMenu('interviews')}
                         >
                           <div className="flex justify-between items-center">
                             <span>{item.label}</span>
                             {activeSubMenu === 'interviews' ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                           </div>
                         </div>
                         {activeSubMenu === 'interviews' && (
                           <ul className="pl-4 ml-2 border-l-2 border-gray-300 dark:border-gray-600">
                             {[
                               { label: "All", path: "/admin/interviews" },
                               { label: "Scheduled", path: "/admin/scheduled-interviews" },
                               { label: "Assigned to me", path: "/admin/my-scheduled-interviews" }
                             ].map(subItem => (
                               <Link to={getRoleBasedRouteLocal(subItem.path)} key={subItem.path}>
                                 <li
                                   onClick={() => setMobileOpen(false)}
                                   className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${isSubMenuActive(subItem.path) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}
                                 >
                                   {subItem.label}
                                 </li>
                               </Link>
                             ))}
                           </ul>
                         )}
                       </div>
                     ) : (
                       <Link to={getRoleBasedRouteLocal(item.path)} key={item.path}>
                         <li
                           onClick={() => setMobileOpen(false)}
                           className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${isActive(getRoleBasedRouteLocal(item.path)) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}
                         >
                           {item.label}
                         </li>
                       </Link>
                     )
                   ))}
                 </ul>
              )}
            </li>
          )}

          {/* File Management */}
          {['Admin'].some(r => roles.includes(r)) && (
            <li className="px-3">
              <div
                className={`flex items-center truncate gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${isCollapsed ? "justify-center" : ""} ${activeMenu === "file-management" || isFileManagementPath() ? "bg-[#0077FE] text-white font-poppins font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
                onClick={() => toggleMenu('file-management')}>

                <svg  xmlns="http://www.w3.org/2000/svg"  width="28"  height="28"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-file-database"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12.75m-4 0a4 1.75 0 1 0 8 0a4 1.75 0 1 0 -8 0" /><path d="M8 12.5v3.75c0 .966 1.79 1.75 4 1.75s4 -.784 4 -1.75v-3.75" /><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" /></svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full truncate">
                    File Management
                    {activeMenu === 'file-management' || isFileManagementPath() ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                )}

              </div>
              {!isCollapsed && activeMenu === 'file-management' && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    { label: "Upload Resumes", path: "/admin/upload-resumes", roles: ['HR'] },
                    // { label: "DOC Generator", path: "/admin/doc-generator", roles: ['HR'] },
                    { label: "Manage Files", path: "/admin/manage-files", roles: ['HR'] },
                  ].map(item => (
                    <Link to={getRoleBasedRouteLocal(item.path)}>
                      <li
                        key={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-sans hover:bg-blue-100 dark:hover:bg-gray-900 truncate rounded-md ${isActive(getRoleBasedRouteLocal(item.path)) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}
                      >
                        {item.label}
                      </li>
                    </Link>
                  ))}
                </ul>
              )}
            </li>
          )}

          {/* Settings */}
          {['Admin'].some(r => roles.includes(r)) && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${isCollapsed ? "justify-center" : ""} ${activeMenu === "settings" || isSettingsPath() ? "bg-[#0077FE] text-white font-poppins font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
                onClick={() => toggleMenu('settings')}
              >
                <svg  xmlns="http://www.w3.org/2000/svg"  width="27"  height="27"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-settings"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" /><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" /></svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Settings
                    {activeMenu === 'settings' || isSettingsPath() ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === 'settings' && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  <Link to={getRoleBasedRouteLocal("/admin/roles")} onClick={() => setMobileOpen(false)}>
                    <li className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${isActive(getRoleBasedRouteLocal("/admin/roles")) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}>
                      Role
                    </li>
                  </Link>
                  <Link to={getRoleBasedRouteLocal("/admin/permissions")} onClick={() => setMobileOpen(false)}>
                    <li className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${isActive(getRoleBasedRouteLocal("/admin/permissions")) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}>
                      Permission
                    </li>
                  </Link>
                </ul>
              )}
            </li>
          )}

          {/* Master */}
          {['Admin'].some(r => roles.includes(r)) && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${isCollapsed ? "justify-center" : ""} ${activeMenu === "master" || isMasterPath() ? "bg-[#0077FE] text-white font-poppins font-semibold" : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"}`}
                onClick={() => toggleMenu('master')}
              >
                <svg  xmlns="http://www.w3.org/2000/svg"  width="27"  height="27"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="2"  strokeLinecap="round"  strokeLinejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-user-shield"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 21v-2a4 4 0 0 1 4 -4h2" /><path d="M22 16c0 4 -2.5 6 -3.5 6s-3.5 -2 -3.5 -6c1 0 2.5 -.5 3.5 -1.5c1 1 2.5 1.5 3.5 1.5z" /><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" /></svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Master
                    {activeMenu === 'master' || isMasterPath() ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === 'master' && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    { label: "Employment-Type", path: "/admin/master/employment-type" },
                    { label: "Departments", path: "/admin/master/departments" },
                    { label: "Designations", path: "/admin/master/designations" },
                    { label: "Branch", path: "/admin/master/workplace" },
                    { label: "State Code", path: "/admin/master/state_code" },
                    { label: "Employee Grade", path: "/admin/master/employee-grades" },
                    { label: "Categories", path: "/admin/master/categories" },
                    { label: "Salary Grade", path: "/admin/master/salary-grade" },
                    { label: "Leave Type", path: "/admin/master/leave-type" },
                    { label: "Shifts", path: "/admin/master/shifts" },
                    { label: "Holidays", path: "/admin/master/holidays" },
                    { label: "Leave Balance Masters", path: "/admin/master/leave-balance-master" },
                    { label: "Forms Masters", path: "/admin/master/forms-master" },
                    { label: "Department HODs", path: "/admin/master/department-hods" },
                    { label: "Approver Modules", path: "/admin/master/approver-modules" },
                    { label: "Approval Level", path: "/admin/master/approval-level" },
                    { label: "Employee Approver", path: "/admin/master/employee-approver" },
                    { label: "Leave Policy", path: "/admin/master/leave-policy" },
                    { label: "Salary Component", path: "/admin/master/salary-component" },
                    { label: "Pay Structure", path: "/admin/master/pay-structure" },
                    { label: "Eligibility Rule", path: "/admin/master/eligibility-rule" },
                    

                  ].map(item => (
                    <Link to={getRoleBasedRouteLocal(item.path)}>
                      <li
                        key={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`py-2 px-2 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 my-1 dark:hover:bg-gray-900 rounded-md ${isActive(getRoleBasedRouteLocal(item.path)) ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500" : "text-gray-700 hover:text-blue-500 dark:hover:text-white"}`}
                      >
                        {item.label}
                      </li>
                    </Link>
                  ))}
                </ul>
              )}
            </li>
          )}

          {/* appearance Toggle */}
          {!isCollapsed && (
            <li className="px-4 py-2">
              <AppearanceToggle />
            </li>  
          )}

        </ul>
      </div>
    </>
  );
};

export default Sidebar;
